import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MenuLinkData } from '@/lib/api';
import { useSiteMenus } from '@/hooks/useSiteMenus';
import { useSiteConfig } from '@/hooks/useSiteConfig';

// Fallbacks shown until the admin-managed menus load (and if the menus API is
// unreachable) — mirror the seeded defaults so there's no layout flash.
const FALLBACK_MENUS: Record<'footer_help' | 'footer_company' | 'footer_policies', MenuLinkData[]> = {
  footer_help: [
    { label: 'My Account', url: '/account', new_tab: false },
    { label: 'My Orders', url: '/orders', new_tab: false },
    { label: 'Contact Us', url: '/help', new_tab: false },
    { label: 'Bulk Orders', url: '/categories', new_tab: false },
  ],
  footer_company: [
    { label: 'About EsyPrints', url: '/about-us', new_tab: false },
    { label: 'Careers', url: '/careers', new_tab: false },
  ],
  footer_policies: [
    { label: 'Terms & Conditions', url: '/terms-and-conditions', new_tab: false },
    { label: 'Privacy Policy', url: '/privacy-policy', new_tab: false },
    { label: 'Returns & Refunds', url: '/returns-and-refunds', new_tab: false },
    { label: 'Shipping Policy', url: '/shipping-policy', new_tab: false },
  ],
};

// External http(s) URLs need an <a>; internal paths use the SPA <Link>.
function MenuItems({ links }: { links: MenuLinkData[] }) {
  return (
    <>
      {links.map((l) => (
        <li key={`${l.label}-${l.url}`}>
          {/^https?:\/\//i.test(l.url) || l.new_tab ? (
            <a href={l.url} target={l.new_tab ? '_blank' : undefined} rel="noopener">
              {l.label}
            </a>
          ) : (
            <Link to={l.url}>{l.label}</Link>
          )}
        </li>
      ))}
    </>
  );
}

export default function Footer() {
  // "Become a Service Provider" is admin-controlled (Settings → Feature
  // toggles). Hidden until the config confirms it should show, so switching
  // it off in the admin panel removes the option on the next page load.
  const siteConfig = useSiteConfig();
  // Footer link columns are admin-managed (Content → Site Menus); the shared
  // hook caches the fetch across Footer / MainNav / MobileNav.
  const siteMenus = useSiteMenus();
  const menus = {
    footer_help: siteMenus?.footer_help?.length ? siteMenus.footer_help : FALLBACK_MENUS.footer_help,
    footer_company: siteMenus?.footer_company?.length ? siteMenus.footer_company : FALLBACK_MENUS.footer_company,
    footer_policies: siteMenus?.footer_policies?.length ? siteMenus.footer_policies : FALLBACK_MENUS.footer_policies,
  };

  // Phones: link columns are accordions (home-mobile.css); desktop ignores this.
  const [openCol, setOpenCol] = useState<string | null>(null);
  const col = (key: string) => ({ className: openCol === key ? 'is-open' : undefined });
  const head = (key: string) => ({ onClick: () => setOpenCol((k) => (k === key ? null : key)), 'aria-expanded': openCol === key });

  const phone = (siteConfig?.support_phone || '').trim();
  const email = (siteConfig?.support_email || '').trim() || 'info@esyprints.com';

  const social = SOCIAL.map((n) => ({ ...n, url: String((siteConfig as Record<string, unknown> | null)?.[`social_${n.key}`] ?? '').trim() }));

  // Vistaprint-style layout in the EsyPrints black/gold theme: brand row,
  // four compact link columns, then a legal bar. No street address (owner
  // request 2026-10-28). Styles: esy-design.css + .vp-footer in esy-theme.css.
  return (
    <div className="esy-d">
    <footer className="esy-footer vp-footer">
      <div className="container">
        <div className="vp-footer__brand">
          <Link to="/" className="footer__logo" aria-label="EsyPrints home"><img src="/images/esy/footer-logo.png" alt="EsyPrints" /></Link>
          <p>India&apos;s hyper-local on-demand print marketplace — printed near you, delivered in hours.</p>
          <ul className="vp-footer__social" aria-label="EsyPrints on social media">
            {social.map((n) => (
              <li key={n.key}>
                {n.url
                  ? <a href={n.url} target="_blank" rel="noopener noreferrer" aria-label={n.label}><SocialIcon d={n.d} /></a>
                  : <span aria-label={n.label} role="img"><SocialIcon d={n.d} /></span>}
              </li>
            ))}
          </ul>
        </div>
        <div className="footer__grid vp-footer__grid">
          <div {...col('help')}>
            <h4 className="footer__heading" {...head('help')}>Let us help</h4>
            <ul className="footer__links"><MenuItems links={menus.footer_help} /></ul>
          </div>
          <div {...col('company')}>
            <h4 className="footer__heading" {...head('company')}>Our company</h4>
            <ul className="footer__links">
              <MenuItems links={menus.footer_company} />
              {siteConfig?.sp_registration_visible && (
                <li><a href={siteConfig.sp_register_url} target="_blank" rel="noopener">Become a Service Provider</a></li>
              )}
            </ul>
          </div>
          <div {...col('policies')}>
            <h4 className="footer__heading" {...head('policies')}>Our policies</h4>
            <ul className="footer__links"><MenuItems links={menus.footer_policies} /></ul>
          </div>
          <div {...col('contact')}>
            <h4 className="footer__heading" {...head('contact')}>Contact</h4>
            <ul className="footer__links vp-footer__contact">
              <li><a href={`mailto:${email}`}>{email}</a></li>
              {phone && <li><a href={`tel:${phone.replace(/[^d+]/g, '')}`}>{phone}</a></li>}
              <li><Link to="/help">Help centre</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer__bottom vp-footer__legal">
          <span>&copy; {new Date().getFullYear()} EsyPrints. All rights reserved.</span>
          <nav aria-label="Legal">
            {menus.footer_policies.slice(0, 3).map((l) => <Link key={l.url} to={l.url}>{l.label}</Link>)}
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Back to top</a>
          </nav>
          <span>Designed by SV Soft Solutions Pvt Ltd</span>
        </div>
      </div>
    </footer>
    </div>
  );
}

const SOCIAL = [
  { key: 'facebook', label: 'Facebook', d: 'M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v8h4v-8h3l.5-4h-3.5V8.8c0-.5.3-.8.5-.8Z' },
  { key: 'instagram', label: 'Instagram', d: 'M12 7.3A4.7 4.7 0 1 0 12 16.7 4.7 4.7 0 0 0 12 7.3Zm0 7.7a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm4.9-8.9a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2ZM12 2c-2.7 0-3 0-4.1.1C4 2.3 2.3 4 2.1 7.9 2 9 2 9.3 2 12s0 3 .1 4.1C2.3 20 4 21.7 7.9 21.9 9 22 9.3 22 12 22s3 0 4.1-.1c3.9-.2 5.6-1.9 5.8-5.8.1-1.1.1-1.4.1-4.1s0-3-.1-4.1C21.7 4 20 2.3 16.1 2.1 15 2 14.7 2 12 2Z' },
  { key: 'youtube', label: 'YouTube', d: 'M22 8.2a3 3 0 0 0-2.1-2.1C18 5.6 12 5.6 12 5.6s-6 0-7.9.5A3 3 0 0 0 2 8.2 31 31 0 0 0 1.6 12 31 31 0 0 0 2 15.8a3 3 0 0 0 2.1 2.1c1.9.5 7.9.5 7.9.5s6 0 7.9-.5a3 3 0 0 0 2.1-2.1c.4-1.2.4-3.8.4-3.8s0-2.6-.4-3.8ZM10 15V9l5.2 3L10 15Z' },
  { key: 'linkedin', label: 'LinkedIn', d: 'M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.5h4V21H3V9.5Zm6.5 0h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.3 0-2.9-1.8-2.9s-2 1.4-2 2.8V21h-4V9.5Z' },
] as const;

function SocialIcon({ d }: { d: string }) {
  return <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d={d} /></svg>;
}
